import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { X, Clock } from 'lucide-react';
import type { User } from '@/types';
import api from '@/lib/api';
import { getUserColor, formatTime12Hour, computeRemainingAvailability, getRecentSlots, saveRecentSlot } from '@/utils/calendarHelpers';
import { DAY_LABELS } from '@/utils/constants';
import { useUndoableDelete } from '@/hooks/useUndoableDelete';
import type { DayData } from './types';
import { ClockInOutButton } from './ClockInOutButton';
import { CareLogPanel } from '@/components/care/CareLogPanel';

interface DayModalProps {
  day: DayData;
  onClose: () => void;
  currentUser: User;
  isParentOrAdmin: boolean;
  isCaretaker: boolean;
  isFamily: boolean;
  queryClient: ReturnType<typeof useQueryClient>;
}

export function DayModal({ day, onClose, currentUser, isParentOrAdmin, isCaretaker, isFamily, queryClient }: DayModalProps) {
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [claimStartTime, setClaimStartTime] = useState('');
  const [claimEndTime, setClaimEndTime] = useState('');
  const [selectedWindow, setSelectedWindow] = useState<{ start: string; end: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assigningWindow, setAssigningWindow] = useState<{ start: string; end: string } | null>(null);
  const [selectedCaretakerId, setSelectedCaretakerId] = useState<string>('');
  const [assignStartTime, setAssignStartTime] = useState('');
  const [assignEndTime, setAssignEndTime] = useState('');
  const [recentSlots, setRecentSlots] = useState<{ start: string; end: string }[]>([]);
  const [reassigningSlotId, setReassigningSlotId] = useState<string | null>(null);
  const [reassignCaretakerId, setReassignCaretakerId] = useState<string>('');
  const [earlyEndSlotId, setEarlyEndSlotId] = useState<string | null>(null);
  const [earlyEndTime, setEarlyEndTime] = useState<string>('');
  const { deleteSlotWithUndo } = useUndoableDelete();

  // Load recent slots on mount
  useEffect(() => {
    if (isParentOrAdmin) {
      setRecentSlots(getRecentSlots());
    }
  }, [isParentOrAdmin]);

  // Fetch caretakers and others for assignment (parent only)
  const { data: assignableUsers = [] } = useQuery({
    queryKey: ['assignable-users'],
    queryFn: async () => {
      const res = await api.get('/users');
      const data = res.data;
      return (data.users || data || []).filter((u: any) => u.role === 'caretaker' || u.role === 'family');
    },
    enabled: isParentOrAdmin,
    staleTime: 1000 * 5,
  });

  // Fetch monthly limit and summary for assignment warnings (parent only)
  const modalNow = new Date();
  const modalYear = modalNow.getFullYear();
  const modalMonth = modalNow.getMonth() + 1;

  const { data: modalLimitData } = useQuery({
    queryKey: ['settings', 'monthly_hours_limit'],
    queryFn: async () => {
      const res = await api.get('/settings/monthly_hours_limit');
      return res.data;
    },
    enabled: isParentOrAdmin,
    staleTime: 1000 * 30,
  });

  const { data: modalSummaryData } = useQuery({
    queryKey: ['monthly-summary', modalYear, modalMonth],
    queryFn: async () => {
      const params = new URLSearchParams({ year: String(modalYear), month: String(modalMonth) });
      const res = await api.get(`/timeslots/monthly-summary?${params}`);
      return res.data;
    },
    enabled: isParentOrAdmin,
    staleTime: 1000 * 30,
  });

  const modalLimit = parseFloat(modalLimitData?.value || '80');
  const modalCurrentTotal = (modalSummaryData?.used_hours || 0) + (modalSummaryData?.scheduled_hours || 0);

  const dateDisplay = format(day.date, 'EEEE, MMMM d, yyyy');

  // Check if this day is in the past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayDate = new Date(day.date);
  dayDate.setHours(0, 0, 0, 0);
  const isPast = dayDate < today;

  // Compute remaining available windows (after subtracting ALL claimed slots including defaults)
  const allClaimedSlots = useMemo(() => day.claimedSlots.filter((s: any) => s.status !== 'cancelled'), [day.claimedSlots]);

  const remainingWindows = useMemo(() => {
    const windows = computeRemainingAvailability(day.availabilityWindows, allClaimedSlots);
    // For today, filter out windows whose end time has already passed
    if (day.isToday) {
      const nowHHMM = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Los_Angeles' });
      return windows.filter((w: { start: string; end: string }) => w.end > nowHHMM);
    }
    return windows;
  }, [day.availabilityWindows, allClaimedSlots, day.isToday]);

  // Create availability (parent only) - uses overrides endpoint
  const handleCreateAvailability = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post('/availability/overrides', {
        date: day.dateString,
        override_type: 'add_windows',
        windows: [{ start: startTime, end: endTime }],
      });
      // Save to recent slots
      saveRecentSlot(startTime, endTime);
      setRecentSlots(getRecentSlots());
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Claim slot (caretaker only)
  const handleClaimSlot = async (slotStartTime: string, slotEndTime: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      const startDatetimeUtc = `${day.dateString}T${slotStartTime}:00.000Z`;
      const endDatetimeUtc = `${day.dateString}T${slotEndTime}:00.000Z`;

      await api.post('/timeslots', {
        date: day.dateString,
        start_time: slotStartTime,
        end_time: slotEndTime,
        start_datetime_utc: startDatetimeUtc,
        end_datetime_utc: endDatetimeUtc,
      });
      queryClient.invalidateQueries({ queryKey: ['timeslots'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle selecting a window to claim partially
  const handleSelectWindow = (window: { start: string; end: string }) => {
    setSelectedWindow(window);
    setClaimStartTime(window.start);
    setClaimEndTime(window.end);
  };

  // Handle assigning a slot to a caretaker (parent only)
  const handleAssignSlot = async (st: string, et: string, caretakerId: string) => {
    if (!caretakerId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post('/timeslots/assign', {
        caretaker_id: caretakerId,
        date: day.dateString,
        start_time: st,
        end_time: et,
      });
      queryClient.invalidateQueries({ queryKey: ['timeslots'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      setAssigningWindow(null);
      setSelectedCaretakerId('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete slot (parent only) — with undo
  const handleDeleteSlot = (slotId: string) => {
    deleteSlotWithUndo(slotId, 'Slot deleted');
  };

  // Reassign a slot to a different caretaker (parent only)
  const handleReassignSlot = async (slotId: string, caretakerId: string) => {
    if (!caretakerId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.patch(`/timeslots/${slotId}/reassign`, { caretaker_id: caretakerId });
      queryClient.invalidateQueries({ queryKey: ['timeslots'] });
      setReassigningSlotId(null);
      setReassignCaretakerId('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // End early: shorten a slot
  const handleEndEarly = async (slotId: string, newEndTime: string) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await api.patch(`/timeslots/${slotId}/end-early`, { new_end_time: newEndTime });
      queryClient.invalidateQueries({ queryKey: ['timeslots'] });
      queryClient.invalidateQueries({ queryKey: ['availability'] });
      setEarlyEndSlotId(null);
      setEarlyEndTime('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div data-testid={`modal-day-${day.dateString.replace(/-/g, '')}`} className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 sticky top-0 bg-slate-800">
          <div>
            <h3 className="text-lg font-semibold text-white">{dateDisplay}</h3>
            <p className="text-sm text-gray-400">
              {isParentOrAdmin ? 'Manage availability' : 'View and claim slots'}
            </p>
          </div>
          <button data-testid="clickable-day-modal-close" onClick={onClose} className="p-1 hover:bg-slate-700 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* CARETAKER / FAMILY VIEW */}
          {(isCaretaker || isFamily) && !isPast && (
            <>
              {/* Available slots to claim */}
              {remainingWindows.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Available - Click to claim</h4>
                  <div className="space-y-2">
                    {remainingWindows.map((window, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg ${
                          selectedWindow?.start === window.start && selectedWindow?.end === window.end ? 'ring-2 ring-emerald-500' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Clock size={16} className="text-emerald-400" />
                          <span className="text-emerald-400">
                            {formatTime12Hour(window.start)} - {formatTime12Hour(window.end)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleClaimSlot(window.start, window.end)}
                            disabled={isSubmitting}
                            data-testid={`clickable-quick-claim-${window.start.replace(':', '')}-${window.end.replace(':', '')}`}
                            className="px-3 py-1 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50"
                          >
                            Claim
                          </button>
                          <button
                            onClick={() => handleSelectWindow(window)}
                            data-testid={`clickable-custom-claim-${window.start.replace(':', '')}-${window.end.replace(':', '')}`}
                            className="px-3 py-1 text-sm bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
                          >
                            Custom
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom time claim form */}
              {selectedWindow && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-400 mb-3">Claim Custom Time</h4>
                  <p className="text-xs text-gray-400 mb-3">
                    Select a portion of {formatTime12Hour(selectedWindow.start)} - {formatTime12Hour(selectedWindow.end)}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Start</label>
                      <input
                        type="time"
                        value={claimStartTime}
                        min={selectedWindow.start}
                        max={selectedWindow.end}
                        onChange={(e) => setClaimStartTime(e.target.value)}
                        data-testid="input-custom-claim-start"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">End</label>
                      <input
                        type="time"
                        value={claimEndTime}
                        min={selectedWindow.start}
                        max={selectedWindow.end}
                        onChange={(e) => setClaimEndTime(e.target.value)}
                        data-testid="input-custom-claim-end"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleClaimSlot(claimStartTime, claimEndTime)}
                      disabled={isSubmitting || !claimStartTime || !claimEndTime}
                      data-testid="clickable-custom-claim-submit"
                      className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? 'Claiming...' : `Claim ${formatTime12Hour(claimStartTime)} - ${formatTime12Hour(claimEndTime)}`}
                    </button>
                    <button
                      onClick={() => setSelectedWindow(null)}
                      data-testid="clickable-custom-claim-cancel"
                      className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Your claimed slots - with unclaim option */}
              {day.claimedSlots.filter((s: any) => s.caretaker_id === currentUser.id && s.status !== 'cancelled').length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Your Slots</h4>
                  <div className="space-y-2">
                    {day.claimedSlots.filter((s: any) => s.caretaker_id === currentUser.id && s.status !== 'cancelled').map((slot: any) => (
                      <div key={slot.id} className="space-y-2">
                        <div
                          className="flex items-center justify-between p-3 rounded-lg"
                          style={{
                            backgroundColor: currentUser.color ? `${currentUser.color}15` : 'rgba(99, 102, 241, 0.15)',
                            borderLeft: currentUser.color ? `3px solid ${currentUser.color}` : '3px solid #6366f1',
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {currentUser.emoji && <span className="text-xl">{currentUser.emoji}</span>}
                            <span style={{ color: currentUser.color || '#818cf8' }}>
                              {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)}
                            </span>
                          </div>
                          {slot.status === 'claimed' && !slot.clock_in_time && (
                            <div className="flex items-center gap-1">
                              {day.isToday && (
                                <button
                                  onClick={() => {
                                    if (earlyEndSlotId === slot.id) {
                                      setEarlyEndSlotId(null);
                                      setEarlyEndTime('');
                                    } else {
                                      setEarlyEndSlotId(slot.id);
                                      setEarlyEndTime('');
                                    }
                                  }}
                                  data-testid={`clickable-end-early-${slot.id}`}
                                  className="px-3 py-1 text-sm bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg transition-colors"
                                >
                                  End Early
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  deleteSlotWithUndo(slot.id, 'Slot unclaimed');
                                  onClose();
                                }}
                                data-testid={`clickable-unclaim-slot-${slot.id}`}
                                className="px-3 py-1 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                              >
                                Unclaim
                              </button>
                            </div>
                          )}
                        </div>
                        <ClockInOutButton slot={slot} currentUserId={currentUser.id} isParentOrAdmin={false} />
                        {/* Care log panel — show when clocked in today */}
                        {slot.clock_in_time && day.isToday && (
                          <div className="p-3 bg-slate-700/30 border border-slate-600/50 rounded-lg">
                            <h5 className="text-xs font-medium text-gray-400 mb-2">Log Care Events</h5>
                            <CareLogPanel date={day.dateString} slotId={slot.id} compact />
                          </div>
                        )}
                        {earlyEndSlotId === slot.id && (
                          <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg space-y-2">
                            <label className="block text-sm text-yellow-400">New end time:</label>
                            <div className="flex gap-2">
                              <input
                                type="time"
                                value={earlyEndTime}
                                min={slot.start_time}
                                max={slot.end_time}
                                onChange={(e) => setEarlyEndTime(e.target.value)}
                                data-testid={`input-end-early-time-${slot.id}`}
                                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                              />
                              <button
                                onClick={() => handleEndEarly(slot.id, earlyEndTime)}
                                disabled={isSubmitting || !earlyEndTime || earlyEndTime <= slot.start_time || earlyEndTime >= slot.end_time}
                                data-testid={`clickable-end-early-confirm-${slot.id}`}
                                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                              >
                                {isSubmitting ? '...' : 'Confirm'}
                              </button>
                              <button
                                onClick={() => { setEarlyEndSlotId(null); setEarlyEndTime(''); }}
                                data-testid={`clickable-end-early-cancel-${slot.id}`}
                                className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded-lg transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                            {earlyEndTime && earlyEndTime > slot.start_time && earlyEndTime < slot.end_time && (
                              <p className="text-xs text-gray-400">
                                Slot will be shortened to {formatTime12Hour(slot.start_time)} - {formatTime12Hour(earlyEndTime)}. The remaining time will become available.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Others' slots */}
              {day.claimedSlots.filter((s: any) => s.caretaker_id !== currentUser.id && s.status !== 'cancelled').length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Claimed by Others</h4>
                  <div className="space-y-2">
                    {day.claimedSlots.filter((s: any) => s.caretaker_id !== currentUser.id && s.status !== 'cancelled').map((slot: any) => (
                      <div
                        key={slot.id}
                        className="flex items-center gap-2 p-3 rounded-lg"
                        style={{
                          backgroundColor: `${getUserColor(slot.caretaker)}15`,
                          borderLeft: `3px solid ${getUserColor(slot.caretaker)}`,
                        }}
                      >
                        {slot.caretaker?.emoji && <span className="text-xl">{slot.caretaker.emoji}</span>}
                        <span style={{ color: getUserColor(slot.caretaker, 'white') }}>
                          {slot.caretaker?.first_name || 'Unknown'}
                        </span>
                        <span className="text-gray-400">
                          {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* PARENT/ADMIN VIEW */}
          {isParentOrAdmin && !isPast && (
            <div className="space-y-2">
              {/* Unclaimed slots first */}
              {remainingWindows.map((window, idx) => (
                <div key={`avail-${idx}`} className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-emerald-400" />
                      <span className="text-emerald-400">
                        {formatTime12Hour(window.start)} - {formatTime12Hour(window.end)}
                      </span>
                      <span className="text-xs text-gray-500">Unclaimed</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {assignableUsers.length > 0 && (
                        <button
                          onClick={() => {
                            if (assigningWindow?.start === window.start && assigningWindow?.end === window.end) {
                              setAssigningWindow(null);
                            } else {
                              setAssigningWindow(window);
                              setAssignStartTime(window.start);
                              setAssignEndTime(window.end);
                            }
                          }}
                          data-testid={`clickable-assign-window-${window.start.replace(':', '')}-${window.end.replace(':', '')}`}
                          className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                        >
                          Assign
                        </button>
                      )}
                      <button
                        data-testid={`clickable-remove-unclaimed-${window.start.replace(':', '')}-${window.end.replace(':', '')}`}
                        onClick={async () => {
                          setIsSubmitting(true);
                          setError(null);
                          try {
                            const remainingWins = day.availabilityWindows.filter(
                              (w: any) => w.start_time !== window.start || w.end_time !== window.end
                            );
                            if (remainingWins.length === 0) {
                              await api.delete(`/availability/overrides/by-date?date=${day.dateString}`);
                              await api.post('/availability/overrides', {
                                date: day.dateString,
                                override_type: 'remove_all',
                              });
                            } else {
                              const windows = remainingWins.map((w: any) => ({ start: w.start_time, end: w.end_time }));
                              await api.post('/availability/overrides', {
                                date: day.dateString,
                                override_type: 'replace_windows',
                                windows,
                              });
                            }
                            queryClient.invalidateQueries({ queryKey: ['availability'] });
                          } catch (err: any) {
                            setError(err.message);
                          } finally {
                            setIsSubmitting(false);
                          }
                        }}
                        disabled={isSubmitting}
                        className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                        title="Remove this time slot"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Assign dropdown */}
                  {assigningWindow?.start === window.start && assigningWindow?.end === window.end && (() => {
                    const selectedUser = assignableUsers.find((u: any) => u.id === selectedCaretakerId);
                    const dayOfWeek = format(day.date, 'EEEE').toLowerCase();
                    const isPreferredDayOff = selectedUser?.preferred_days_off?.includes(dayOfWeek);
                    const isUnavailableDay = selectedUser?.unavailable_days?.includes(dayOfWeek);
                    // Monthly limit warning using custom time
                    const [sh, sm] = assignStartTime.split(':').map(Number);
                    const [eh, em] = assignEndTime.split(':').map(Number);
                    const proposedHours = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
                    const wouldExceed = modalCurrentTotal + proposedHours > modalLimit;
                    return (
                      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg ml-4 space-y-2">
                        <label className="block text-sm text-gray-400 mb-2">Assign to:</label>
                        {/* Custom time range within the window */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Start</label>
                            <input
                              type="time"
                              value={assignStartTime}
                              min={window.start}
                              max={assignEndTime}
                              onChange={(e) => setAssignStartTime(e.target.value)}
                              data-testid="input-assign-start-time"
                              className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">End</label>
                            <input
                              type="time"
                              value={assignEndTime}
                              min={assignStartTime}
                              max={window.end}
                              onChange={(e) => setAssignEndTime(e.target.value)}
                              data-testid="input-assign-end-time"
                              className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <select
                            value={selectedCaretakerId}
                            onChange={(e) => setSelectedCaretakerId(e.target.value)}
                            data-testid="select-assign-caretaker"
                            className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select person...</option>
                            {assignableUsers.map((u: any) => (
                              <option key={u.id} value={u.id}>
                                {u.emoji || ''} {u.first_name} {u.last_name || ''} {u.role === 'family' ? '(Family)' : ''}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleAssignSlot(assignStartTime, assignEndTime, selectedCaretakerId)}
                            disabled={isSubmitting || !selectedCaretakerId}
                            data-testid="clickable-assign-confirm"
                            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                          >
                            {isSubmitting ? '...' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => {
                              setAssigningWindow(null);
                              setSelectedCaretakerId('');
                            }}
                            data-testid="clickable-assign-cancel"
                            className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                        {wouldExceed && (
                          <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-400 text-xs">
                            This assignment would exceed the monthly limit of {modalLimit} hours (currently at {modalCurrentTotal.toFixed(1)}/{modalLimit}).
                          </div>
                        )}
                        {isUnavailableDay && (
                          <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs">
                            {selectedUser.first_name} has marked {DAY_LABELS[dayOfWeek] || dayOfWeek} as a day they cannot work.
                          </div>
                        )}
                        {isPreferredDayOff && !isUnavailableDay && (
                          <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-400 text-xs">
                            {selectedUser.first_name} prefers {DAY_LABELS[dayOfWeek] || dayOfWeek} off.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}

              {/* Claimed slots (real + default/virtual) */}
              {day.claimedSlots.filter((s: any) => s.status !== 'cancelled').map((slot: any) => {
                const reassignDayOfWeek = format(day.date, 'EEEE').toLowerCase();
                const reassignSelectedUser = assignableUsers.find((u: any) => u.id === reassignCaretakerId);
                const reassignIsPreferredOff = reassignSelectedUser?.preferred_days_off?.includes(reassignDayOfWeek);
                const reassignIsUnavailable = reassignSelectedUser?.unavailable_days?.includes(reassignDayOfWeek);
                return (
                  <div key={slot.id} className="space-y-2">
                    <div
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{
                        backgroundColor: `${getUserColor(slot.caretaker)}15`,
                        borderLeft: `3px ${slot.is_default ? 'dashed' : 'solid'} ${getUserColor(slot.caretaker)}`,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        {slot.caretaker?.emoji && <span className="text-xl">{slot.caretaker.emoji}</span>}
                        <span style={{ color: getUserColor(slot.caretaker, 'white') }}>
                          {slot.caretaker?.first_name || 'Unknown'}
                        </span>
                        {slot.is_default && <span className="text-xs text-gray-500">(Default)</span>}
                        <span className="text-gray-400">
                          {formatTime12Hour(slot.start_time)} - {formatTime12Hour(slot.end_time)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {slot.is_default ? (
                          <button
                            onClick={() => {
                              setAssigningWindow({ start: slot.start_time, end: slot.end_time });
                              setAssignStartTime(slot.start_time);
                              setAssignEndTime(slot.end_time);
                            }}
                            data-testid={`clickable-assign-default-${slot.id}`}
                            className="px-2 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded transition-colors"
                          >
                            Assign
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setReassigningSlotId(reassigningSlotId === slot.id ? null : slot.id);
                                setReassignCaretakerId('');
                                if (earlyEndSlotId === slot.id) { setEarlyEndSlotId(null); setEarlyEndTime(''); }
                              }}
                              data-testid={`clickable-reassign-slot-${slot.id}`}
                              className="px-2 py-1 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded transition-colors"
                            >
                              Reassign
                            </button>
                            {day.isToday && (
                              <button
                                onClick={() => {
                                  if (earlyEndSlotId === slot.id) {
                                    setEarlyEndSlotId(null);
                                    setEarlyEndTime('');
                                  } else {
                                    setEarlyEndSlotId(slot.id);
                                    setEarlyEndTime('');
                                    if (reassigningSlotId === slot.id) { setReassigningSlotId(null); setReassignCaretakerId(''); }
                                  }
                                }}
                                data-testid={`clickable-end-early-${slot.id}`}
                                className="px-2 py-1 text-xs bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded transition-colors"
                              >
                                End Early
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteSlot(slot.id)}
                              disabled={isSubmitting}
                              data-testid={`clickable-unassign-slot-${slot.id}`}
                              className="px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors disabled:opacity-50"
                            >
                              Unassign
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* End Early form */}
                    {earlyEndSlotId === slot.id && (
                      <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg ml-4 space-y-2">
                        <label className="block text-sm text-yellow-400">New end time:</label>
                        <div className="flex gap-2">
                          <input
                            type="time"
                            value={earlyEndTime}
                            min={slot.start_time}
                            max={slot.end_time}
                            onChange={(e) => setEarlyEndTime(e.target.value)}
                            data-testid={`input-end-early-time-${slot.id}`}
                            className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                          <button
                            onClick={() => handleEndEarly(slot.id, earlyEndTime)}
                            disabled={isSubmitting || !earlyEndTime || earlyEndTime <= slot.start_time || earlyEndTime >= slot.end_time}
                            data-testid={`clickable-end-early-confirm-${slot.id}`}
                            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                          >
                            {isSubmitting ? '...' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => { setEarlyEndSlotId(null); setEarlyEndTime(''); }}
                            data-testid={`clickable-end-early-cancel-${slot.id}`}
                            className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                        {earlyEndTime && earlyEndTime > slot.start_time && earlyEndTime < slot.end_time && (
                          <p className="text-xs text-gray-400">
                            Slot will be shortened to {formatTime12Hour(slot.start_time)} - {formatTime12Hour(earlyEndTime)}. The remaining time will become available.
                          </p>
                        )}
                      </div>
                    )}
                    {/* Reassign dropdown */}
                    {reassigningSlotId === slot.id && (
                      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg ml-4 space-y-2">
                        <label className="block text-sm text-gray-400 mb-2">Reassign to:</label>
                        <div className="flex gap-2">
                          <select
                            value={reassignCaretakerId}
                            onChange={(e) => setReassignCaretakerId(e.target.value)}
                            data-testid="select-reassign-caretaker"
                            className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select person...</option>
                            {assignableUsers
                              .filter((u: any) => u.id !== slot.caretaker_id)
                              .map((u: any) => (
                                <option key={u.id} value={u.id}>
                                  {u.emoji || ''} {u.first_name} {u.last_name || ''} {u.role === 'family' ? '(Family)' : ''}
                                </option>
                              ))}
                          </select>
                          <button
                            onClick={() => handleReassignSlot(slot.id, reassignCaretakerId)}
                            disabled={isSubmitting || !reassignCaretakerId}
                            data-testid="clickable-reassign-confirm"
                            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                          >
                            {isSubmitting ? '...' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => {
                              setReassigningSlotId(null);
                              setReassignCaretakerId('');
                            }}
                            data-testid="clickable-reassign-cancel"
                            className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                        {reassignIsUnavailable && (
                          <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs">
                            {reassignSelectedUser.first_name} has marked {DAY_LABELS[reassignDayOfWeek] || reassignDayOfWeek} as a day they cannot work.
                          </div>
                        )}
                        {reassignIsPreferredOff && !reassignIsUnavailable && (
                          <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-400 text-xs">
                            {reassignSelectedUser.first_name} prefers {DAY_LABELS[reassignDayOfWeek] || reassignDayOfWeek} off.
                          </div>
                        )}
                      </div>
                    )}
                    {!slot.is_default && (
                      <ClockInOutButton slot={slot} currentUserId={currentUser.id} isParentOrAdmin={isParentOrAdmin} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* No availability message - different for past vs future */}
          {!isPast && remainingWindows.length === 0 && day.claimedSlots.filter((s: any) => s.status !== 'cancelled').length === 0 && (
            <div className="text-center py-6 text-gray-400">
              {isParentOrAdmin
                ? 'No shifts scheduled for this day. Add a shift below.'
                : 'No availability for this day.'}
            </div>
          )}

          {/* All slots claimed message - only for future */}
          {!isPast && remainingWindows.length === 0 && day.availabilityWindows.length > 0 && !isParentOrAdmin && (
            <div className="text-center py-4 text-yellow-400 text-sm bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              All available time has been claimed for this day.
            </div>
          )}

          {/* Create availability form (parent only) - only for future/today */}
          {!isPast && isParentOrAdmin && (
            <div className="pt-4 border-t border-slate-700">
              <h4 className="text-sm font-medium text-white mb-3">
                {day.availabilityWindows.length > 0 ? 'Add Another Shift' : 'Add Shift'}
              </h4>

              {/* Recent time slots */}
              {recentSlots.length > 0 && (
                <div className="mb-3">
                  <label className="block text-xs text-gray-400 mb-2">Recent Time Slots</label>
                  <div className="flex flex-wrap gap-2">
                    {recentSlots.map((slot, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setStartTime(slot.start);
                          setEndTime(slot.end);
                        }}
                        data-testid={`clickable-recent-slot-${idx}`}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          startTime === slot.start && endTime === slot.end
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                        }`}
                      >
                        {formatTime12Hour(slot.start)} - {formatTime12Hour(slot.end)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    data-testid="input-create-availability-start"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">End Time</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    data-testid="input-create-availability-end"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <button
                onClick={handleCreateAvailability}
                disabled={isSubmitting}
                data-testid="clickable-add-shift"
                className="w-full py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Creating...' : 'Add Shift'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
